import { formatDate } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import {
  formatDateTimeStamp,
  getEventUpdateDetails,
  getScheduleCellKey,
  getStartEndDateTime,
  getTimerValueDuration,
  getTimerValueDurationFromMilliSeconds,
  getUserReadableDateTimeFormat,
  isLargeScreen,
} from '../../../global.variable';
import {
  getEventAssignees,
  getJobServiceType,
  getJobStatus,
  getScheduleStatus,
  isCurrentOrFutureJob,
  trashIconIf,
} from '../../../roster-common-functions';

@Pipe({
  name: 'misc',
  standalone: false,
})
export class MiscPipe implements PipeTransform {
  barChartDefaultStyle = {
    'width': '100%',
    'background-color': 'var(--bs-primary) !important',
  };
  largeView: Boolean = isLargeScreen;
  formatDateWithoutTime = (date: any) => {
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  getXDaysAgo(timestamp: any): number {
    const eventDate = timestamp ? new Date(timestamp) : new Date();
    const today = new Date();
    const diffMins = today.getTime() - eventDate.getTime();
    return Math.floor(diffMins / (1000 * 60 * 60 * 24));
  }
  getUserReadableChatLastSentDateFormat(timestamp: any): string {
    const eventDate = timestamp ? new Date(timestamp) : new Date();
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    if (eventDate.toDateString() === today.toDateString()) {
      return '';
    } else if (eventDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else if (eventDate.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return formatDateTimeStamp(eventDate, 'd MMM y', 'en_US');
    }
  }
  pad(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }
  formatList(items: any[]): string {
    if (!items?.length) return '';

    if (items.length === 1) {
      return items[0];
    }

    return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
  }
  getOrdinal(n: number | string): string {
    const num = Number(n);

    if (isNaN(num)) return '';

    const s = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;

    return num + (s[(v - 20) % 10] || s[v] || s[0]);
  }
  transform(value: any, action: string, ...args: unknown[]) {
    switch (action) {
      case 'getScheduleCellKey':
        const scheduleDate: any = args[0];

        return getScheduleCellKey(value, scheduleDate);
      case 'isPastScheduleCheck':
        const d = new Date(value);
        d.setHours(0, 0, 0, 0);

        const currentDay = new Date();
        currentDay.setHours(0, 0, 0, 0);

        return d.getTime() < currentDay.getTime();

      case 'getHistory':
        return value?.history_changes;

      case 'getHoursFromMilliSeconds':
        return getTimerValueDurationFromMilliSeconds(value);

      case 'promptValue':
        return value?.value;

      case 'sliceWord':
        const worldSplitLength: any = args[0] || 10;
        return value?.length > worldSplitLength
          ? `${value?.slice(0, worldSplitLength)}...`
          : value;

      case 'promptLabel':
        const promptList: any = args[0];
        const promptInfo: any = promptList?.find(
          (promptItem: any) => promptItem?.value === value?.promptKeyValue,
        );
        return promptInfo?.promptLabel || value?.promptKeyValue;

      case 'convertIntoCent':
        return Math.round(value * 100 * 100) / 100;

      case 'convertIntoDollar':
        return Math.round(value * 100) / 100;

      case 'lastCommentEditedDateTime':
        let sortedComments = value?.sort(
          (a: any, b: any) =>
            new Date(a?.updated_at).getTime() -
            new Date(b?.updated_at).getTime(),
        );
        return sortedComments[sortedComments?.length - 1];

      case 'getBarStyle':
        const barChartInfo: any = args[0];
        const maxChartValue: any = args[1];
        const maxChartSize: any = args[2];
        const barChartValueKey: any = barChartInfo?.value?.name;

        const barHeight = `${
          maxChartSize * (value?.[barChartValueKey] / maxChartValue)
        }px`;
        let newStyle = {
          ...this.barChartDefaultStyle,
          ...barChartInfo?.value?.style,
          height: barHeight,
        };

        return newStyle;

      case 'getBarLabel':
        const barChartLabelInfo: any = args[0];
        const barChartLabelKey: any = barChartLabelInfo?.label?.name;
        let barLabel = value?.[barChartLabelKey];
        if (barChartLabelInfo?.label?.dateTimeObjFormat && barLabel)
          return formatDateTimeStamp(
            new Date(barLabel),
            barChartLabelInfo?.label?.dateTimeObjFormat || 'd MMM y',
            'en_US',
          );

        return barLabel;

      case 'getBarValue':
        const barChartValueInfo: any = args[0];
        const barChartValue: any = barChartValueInfo?.value?.name;

        return value?.[barChartValue];

      case 'getFilterHeader':
        if (!value?.header) {
          return value?.name?.split('_')?.join(' ');
        }
        return value?.header;

      case 'getScheduleTimeRange':
        if (!value) return 'Alarm Response';
        function stripSeconds(time: any) {
          const timeList = time?.split(':');
          timeList?.pop();
          return timeList?.join(':');
        }
        return `${stripSeconds(value?.start_time)} -  ${stripSeconds(
          value?.end_time,
        )}`;

      case 'latLonCenter':
        let lastItem = value[value?.length - 1];

        return { lat: lastItem?.latitude, lon: lastItem?.longitude };

      case 'getWeekDay':
        const date = new Date(Number(value));
        let weekDays = [
          'Sunday',
          'Monday',
          'Tuesday',
          'Wednesday',
          'Thursday',
          'Friday',
          'Saturday',
        ];

        return this.largeView
          ? weekDays[date.getDay()]
          : weekDays[date.getDay()]?.slice(0, 2);

      case 'getFinalPaymentAmount':
        const totalAddedUsers: any = args[0];
        const reservedUserPricing: any = args[1];
        return (
          (value?.app_coupon?.payable_amount
            ? value?.app_coupon?.payable_amount
            : value?.plan?.final_amount) +
          (totalAddedUsers > value?.plan?.allowed_users
            ? (totalAddedUsers - value?.plan?.allowed_users) *
              value?.plan?.user_cost
            : 0) +
          (reservedUserPricing?.total_amount || 0)
        );

      case 'getJobStatus':
        return (
          value?.status_dict?.name || getJobStatus(value, args[0], args[1])
        );

      case 'getScheduleStatus':
        const shiftType: any = args[2];
        return (
          value?.status_dict?.name ||
          getScheduleStatus(value, args[0], args[1], shiftType)
        );

      case 'getJobServiceType':
        return getJobServiceType(value, args[0]);

      case 'getEventAssignees':
        return getEventAssignees(value, args[0], args[1]);

      case 'trashIconIf':
        return trashIconIf(value, args[0], args[1]);

      case 'isCurrentOrFutureJob':
        return isCurrentOrFutureJob(value, args[0], args[1]);

      case 'getConflictSchedules':
        return value?.conflictSchedules || [];

      case 'getPatrolCheckpointName':
        const date2: any = args[0];
        return value?.patrol_route_details?.schedule_details?.name
          ? value?.patrol_route_details?.schedule_details?.name
          : date2
            ? value?.patrol_route_details?.schedule_details?.[
                formatDate(new Date(date2), 'yyyy-MM-dd', 'en_US')
              ]?.name
            : value?.patrol_route_details?.name;

      case 'getTimeDifference':
        let [startTime, endTime] = getStartEndDateTime(value);

        return getTimerValueDuration(startTime, endTime);

      case 'jsonParse':
        function isDictionary(item: any): boolean {
          return (
            item && typeof item === 'object' && item.constructor === Object
          );
        }
        try {
          return isDictionary(value) ? value : JSON.parse(value);
        } catch (error) {
          return {};
        }

      case 'getWeatherFromDate':
        const selectedDate: any = args[0];

        if (!selectedDate) return null;
        return value?.[
          formatDate(new Date(selectedDate), 'yyyy-MM-dd', 'en_US')
        ];
      case 'excludeDateFilters':
        const excludeDateFilters = [
          ...value?.filter((item: any) => !item?.datetimeObj),
        ];
        return excludeDateFilters?.length > 0;

      case 'getEventUpadteFilters':
        const selectedEventFrom: any = args[0];
        const details = getEventUpdateDetails(value, selectedEventFrom);
        return details;

      case 'formatStartEndTime':
        if (!value) return null;
        const splitTimes = value?.split(':');
        return `${splitTimes[0]}:${splitTimes[1]}`;

      case 'eventDateGrouping':
        return getUserReadableDateTimeFormat(value);

      case 'getInitials':
        const userNameList = value?.split(' ');
        if (!userNameList?.length) return null;
        if (userNameList?.length > 1) {
          return `${userNameList[0]?.charAt(0)}${userNameList[
            userNameList?.length - 1
          ]?.charAt(0)}`;
        }
        return value?.charAt(0);

      case 'mapHeightCheck':
        // define the height of the map depending on the map id

        if (value === 'alertMapId') {
          return { height: '30vh' };
        }
        if (value === 'checkpointEditmapid') {
          return { height: '40vh' };
        }
        if (value?.startsWith('smallMapId')) {
          return { height: '30vh' };
        }
        if (value?.startsWith('verySmallMapId')) {
          return { height: '15vh' };
        }
        return {};

      case 'addRosterShiftDateCheck':
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return new Date(value).getTime() >= today.getTime();

      case 'generateLinks':
        if (!value) return [];

        const urlRegex = /(https?:\/\/[^\s<>"')\]]+)/g;
        const parts = value?.split(urlRegex);
        return parts?.map((part: string) =>
          urlRegex.test(part) ? { url: part } : part,
        );
      case 'getJobStatusStyle':
        let statusStyle = {};
        if (value) {
          const statusData: any = args[0];
          statusData?.forEach((element: any) => {
            if (element.name === value) {
              statusStyle = {
                'color': `${element.additional_info?.style?.snubbed?.color}`,
                'background-color': `${element.additional_info?.style?.snubbed['background-color']}`,
                'border': '1px solid',
                'border-radius': '5px',
                'padding': '0.25rem 0.5rem',
                'float': 'right',
                'font-size': 'small',
                'margin': 'auto',
              };
            }
          });
        }
        return statusStyle;
      case 'serverReceivedDateTimeCheck':
        // check if the client recorded time and server recieved time is different by half a minute, then show the server time
        const serverDateTime = value?.server_datetime || value?.updated_at;
        if (!serverDateTime) return false;

        const shownDateTime = value?.updated_datetime || value?.updated_at;
        if (!shownDateTime) return false;

        const timeDiff = Math.abs(
          new Date(serverDateTime)?.getTime() -
            new Date(shownDateTime)?.getTime(),
        );
        if (timeDiff > 30000) return serverDateTime;

        return false;
      case 'canDeleteRunSheet':
        return new Date(value?.start_datetime).getTime() > new Date().getTime();

      case 'runsheetInProgressCheck':
        return (
          new Date(value?.start_datetime).getTime() <= new Date().getTime()
        );

      case 'getXDaysAgo':
        return this.getXDaysAgo(value);

      case 'userReadableDateTimeFormat':
        const endDate = new Date(value);
        endDate.setHours(0, 0, 0, 0);
        let eventDate: any = args[0];
        eventDate = eventDate ? new Date(eventDate) : new Date();
        eventDate.setHours(0, 0, 0, 0);
        return `${this.getUserReadableChatLastSentDateFormat(
          value,
        )} ${formatDateTimeStamp(value, 'HH:mm', 'en_US')} `;

      case 'runsheetDueDateTimeHomePage':
        const runsheetSchedule = value;

        const runSheetEndDateTime = new Date(
          (value?.event_date || value?.end_day) + ' ' + value?.end_time,
        );
        const runSheetStartDateTime = new Date(
          (value?.event_date || value?.start_day) + ' ' + value?.start_time,
        );

        if (
          runsheetSchedule?.start_time > runsheetSchedule?.end_time &&
          runsheetSchedule?.repeat_type != 'once'
        ) {
          runSheetEndDateTime.setDate(runSheetEndDateTime.getDate() + 1);
        }
        const scheduleEndDateTime = new Date(runSheetEndDateTime);
        const isTaskScheduleEnded =
          new Date().getTime() > runSheetEndDateTime?.getTime();
        runSheetEndDateTime.setHours(0, 0, 0, 0);
        runSheetStartDateTime.setHours(0, 0, 0, 0);
        return [
          isTaskScheduleEnded,
          `${getUserReadableDateTimeFormat(
            scheduleEndDateTime,
          )} ${formatDateTimeStamp(scheduleEndDateTime, 'HH:mm', 'en_US')} `,
        ];
      case 'isInProgressCheck':
        // this is to add additional styling to home job cards
        const eventSchedule = value?.schedule_details;
        if (!eventSchedule?.repeat_type) return false;
        const eventEndDateTime = new Date(
          (eventSchedule?.event_date || eventSchedule?.end_day) +
            ' ' +
            eventSchedule?.end_time,
        );
        const eventStartDateTime = new Date(
          (eventSchedule?.event_date || eventSchedule?.start_day) +
            ' ' +
            eventSchedule?.start_time,
        );

        if (
          eventSchedule?.start_time > eventSchedule?.end_time &&
          eventSchedule?.repeat_type != 'once'
        ) {
          eventEndDateTime.setDate(eventEndDateTime.getDate() + 1);
        }

        const currentTime = new Date().getTime();
        return (
          // job start - 90 minutes and end time + 60 minutes
          eventStartDateTime.getTime() - 90 * 60 * 1000 <= currentTime &&
          currentTime <= eventEndDateTime.getTime() + 60 * 60 * 1000
        );
      case 'formatLastSeenDateTime':
        const lastSeenTimeDiff =
          new Date().getTime() - new Date(value).getTime();

        if (lastSeenTimeDiff < 0) return 'just now';

        const diffMins = Math.floor(lastSeenTimeDiff / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30); // approx

        if (diffMins < 1) return 'just now';
        if (diffMins < 60)
          return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
        if (diffHours < 24)
          return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
        if (diffDays < 7)
          return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
        if (diffWeeks < 4)
          return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
        if (diffMonths <= 3)
          return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;

        return 'long ago';
      case 'chatReadMembers':
        const readMembers: any = args[0];
        const readMemberCount = value?.filter((id: any) => id in readMembers);

        return readMemberCount?.length;

      case 'chatSearchTerm':
        if (!value) return [];

        const chatFilterValue = args[0];
        const regex = new RegExp(`(${chatFilterValue})`, 'gi');

        const chatParts = value?.split(regex);
        return chatParts?.map((part: string) =>
          regex.test(part) ? { text: part, higlighted: true } : { text: part },
        );
      case 'runsheetTaskTiming':
        const taskSchedule = value;
        if (
          this.formatDateWithoutTime(new Date(taskSchedule?.start_datetime)) ==
          this.formatDateWithoutTime(new Date(taskSchedule?.end_datetime))
        ) {
          return `${formatDateTimeStamp(
            new Date(taskSchedule?.start_datetime),
            'd MMM y',
            'en_US',
          )} ${formatDateTimeStamp(
            new Date(taskSchedule?.start_datetime),
            'HH:mm',
            'en_US',
          )}`;
        }
        return `${formatDateTimeStamp(
          new Date(taskSchedule?.start_datetime),
          'd MMM y HH:mm',
          'en_US',
        )}`;
      case 'rosterShiftDictEventDateData':
        const shiftDate: any = args[0];

        return value?.[formatDate(shiftDate, 'yyyy-MM-dd', 'en_US')];

      case 'getDictKeys':
        if (!value) return [];
        return Object.keys(value);

      case 'checkSameJobTypeExists':
        const jobTypeId = args[0];

        return (
          value?.filter((job: any) => job?.job_type_id == jobTypeId)?.length > 1
        );
      case 'customTableColumsFilterExists':
        return value?.filter((col: any) => col?.searchValue);

      case 'formatTableHeader':
        return value?.split('_')?.join(' ');

      case 'shiftTimeRemaining':
        const nowTime = new Date().getTime();

        let seconds = Math.floor((value - nowTime) / 1000);
        let hours = Math.floor(seconds / 3600);
        let minutes = Math.floor((seconds % 3600) / 60);

        // if (hours) return `${hours}:${this.pad(minutes)}`;

        return Math.floor((value - nowTime) / 60000);
      case 'promptInChecklist':
        return value?.tasks?.some((task: any) => task?.user_prompt_id);

      case 'shiftSummary':
        const showForOneTimeShifts = !!args[0];

        const {
          repeat_type,
          repeat_interval,
          start_time,
          end_time,
          start_day,
          end_day,
          position,
          week_day,
          month_repeat_case,
          repeat_details,
        } = value;

        const selectedDays: any = repeat_details || [];
        const selectedMonthType: any = month_repeat_case;

        const interval = Number(repeat_interval) || 1;

        let text = `Shift will be `;

        if (repeat_type == 'once') {
          if (!showForOneTimeShifts) return '';

          text += `between ${formatDateTimeStamp(start_day, 'd MMM y ', 'en_US')} ${start_time} and ${formatDateTimeStamp(end_day, 'd MMM y ', 'en_US')} ${end_time}`;
          return text;
        }

        // Time
        text += `between ${start_time} and ${end_time}`;

        if (start_time > end_time) {
          text += ` the following day`;
        }

        text += `,`;

        // Repeat logic
        switch (repeat_type) {
          case 'daily':
            text +=
              interval === 1
                ? ` and will repeat every day`
                : ` and will repeat every ${interval} days`;
            break;

          case 'weekly':
            text +=
              interval === 1
                ? ` and will repeat every week`
                : ` and will repeat every ${interval} weeks`;

            if (selectedDays?.length) {
              text += ` on ${this.formatList(selectedDays)}`;
            }

            break;

          case 'monthly':
            text +=
              interval === 1
                ? ` and will repeat every month`
                : ` and will repeat every ${interval} months`;

            // based by dates
            if (selectedMonthType === 'day' && selectedDays?.length) {
              text += ` on day ${this.formatList(selectedDays)}`;
            }

            // based on postion
            if (selectedMonthType === 'date' && position && week_day) {
              text += ` on the ${this.getOrdinal(position)} ${week_day}`;
            }

            break;

          default:
            break;
        }

        if (start_day && end_day) {
          text += ` starting on ${formatDateTimeStamp(start_day, 'd MMM y ', 'en_US')} and ending on ${formatDateTimeStamp(end_day, 'd MMM y ', 'en_US')}`;
        }

        return text;

      case 'hasShiftStarted':
        const shiftStartDateTime = new Date(
          value?.event_date + ' ' + value?.start_time,
        );
        return (
          !value?.draftedItem &&
          shiftStartDateTime.getTime() < new Date().getTime()
        );

      case 'checkItemsInRowExists':
        const shiftKey: any = args[1];
        const shiftRow: any = args[0];
        return value?.some((shift: any) => shift?.[shiftKey] == shiftRow?.id);

      case 'getScheduleRowHeaders':
        //(row?.user_list?.tag_ids || [])?.includes(userListFilterId) ||
        // (row?.user_list?.job_type_ids || [])?.includes(
        //   userListFilterId
        // )
        const hasShifts = args[0];
        const shiftDataGroupBy = args[1];
        const userListFilterId = args[2];
        let headerFiltersIds: any = args[3] || [];
        const _sourceType: any = args[4] || 'view';
        headerFiltersIds = new Set(
          headerFiltersIds?.map((item: any) => item?.id),
        );
        let returnHeaders: any = [...(value || [])];

        if (shiftDataGroupBy == 'person') {
          if (userListFilterId) {
            returnHeaders = returnHeaders?.filter(
              (header: any) =>
                (header?.user_list?.tag_ids || [])?.includes(
                  userListFilterId,
                ) ||
                (header?.user_list?.job_type_ids || [])?.includes(
                  userListFilterId,
                ),
            );
          } else if (_sourceType != 'print') {
            returnHeaders = [
              { id: 0, full_name: 'Unassigned' },
              ...returnHeaders,
            ];
          }
        }

        if (headerFiltersIds?.size) {
          returnHeaders = returnHeaders?.filter((item: any) =>
            headerFiltersIds?.has(item?.id),
          );
        }

        if (hasShifts && shiftDataGroupBy != 'site')
          // adding total hours field if shifts are in the week
          returnHeaders = [
            ...returnHeaders,
            {
              id: -1,
              [shiftDataGroupBy == 'person' ? 'full_name' : 'company_name']:
                'Total Hours',
            },
          ];

        return returnHeaders;
    }

    return null;
  }
}
